// 🔴 VISITE RÉNOVATION — blocs du relevé : note de visite, photos, clôture,
// relevés du dossier (en cours / clos), code d'accès terrain (bureau).
//
// 🔑 Tous passent par l'objet `api` reçu (bureau OU terrain, cf. ./api.js) :
//    ⛔ aucune URL écrite ici, ⛔ aucune route hors `/api/chantier/*`.
// 🔑 NOTE DE VISITE = note du RELEVÉ, ⛔ le commentaire d'un poste.
// 🔑 PHOTOS affichées depuis un BLOB chargé AVEC l'autorisation : ⛔ aucune URL
//    média publique (une URL nue sans jeton ⇒ 401).
// ⛔ Relevé clos ⇒ lecture seule (le serveur refuse de toute façon).
import { memo, useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import * as filePhotos from "./photosEnAttente";
import { enregistrerSurLeTelephone } from "./copieTelephone";
import { Button } from "../../components/ui/button";

export function Encart({ ton = "info", testId, children }) {
  return (
    <div data-testid={testId} className="text-sm"
         style={{ background: `var(--oh-${ton}-bg)`, color: `var(--oh-${ton}-text)`,
                  border: `0.5px solid var(--oh-${ton}-border)`, borderRadius: 8,
                  padding: 12, lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

const CARTE = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                borderRadius: 12, padding: 16, display: "grid", gap: 8, color: "var(--oh-text)" };
const PRIMAIRE = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" };
const SECONDAIRE = { borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" };
const DISCRET = { borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" };
const heure = (iso) => { try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const date = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso || ""; } };

// ── NOTE DE VISITE ──────────────────────────────────────────────────────────
export function NoteVisite({ api, releveId, lectureSeule }) {
  const [texte, setTexte] = useState("");
  const [enregistre, setEnregistre] = useState(null);   // { texte, modifie_le, rev }
  const [etat, setEtat] = useState("");
  // 🔴 N°894 — conflit : la note a été enregistrée ailleurs depuis son chargement.
  const [conflit, setConflit] = useState(false);
  const [texteNonEnregistre, setTexteNonEnregistre] = useState("");

  const charger = useCallback(async () => {
    const n = await api.lireNote(releveId);
    setTexte(n.texte || ""); setEnregistre(n);
  }, [api, releveId]);

  useEffect(() => {
    let vivant = true;
    api.lireNote(releveId).then((n) => { if (vivant) { setTexte(n.texte || ""); setEnregistre(n); } })
      .catch((err) => vivant && setEtat(err.message));
    return () => { vivant = false; };
  }, [api, releveId]);

  const modifie = enregistre && texte !== (enregistre.texte || "");
  const enregistrer = async () => {
    setEtat("");
    try { setEnregistre(await api.ecrireNote(releveId, texte, enregistre?.rev)); }
    catch (err) {
      // ⛔ fusion, ⛔ nouvel essai automatique : l'humain actualise puis réenregistre.
      if (err?.statut === 409) setConflit(true); else setEtat(err.message);
    }
  };
  const actualiser = async () => {
    setEtat("");
    const saisie = texte;
    try { await charger(); setConflit(false); setTexteNonEnregistre(saisie); }
    catch (err) { setEtat(err.message); }
  };

  return (
    <section data-testid="note-visite" style={CARTE}>
      <div style={{ fontWeight: 500 }}>Note de visite</div>
      {lectureSeule ? (
        <p data-testid="note-visite-lecture" className="text-sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {texte || "Aucune note."}
        </p>
      ) : (
        <>
          <textarea data-testid="note-visite-texte" rows={4} value={texte} aria-label="Note de visite"
                    placeholder="Accès, contraintes, remarques générales sur la visite…"
                    onChange={(ev) => setTexte(ev.target.value)}
                    style={{ borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: 8,
                             background: "var(--oh-bg)", color: "var(--oh-text)" }} />
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Button data-testid="note-visite-enregistrer" variant="outline" onClick={enregistrer}
                    disabled={!modifie || conflit} style={SECONDAIRE}>
              Enregistrer la note
            </Button>
            <span data-testid="note-visite-statut" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
              {modifie ? "Modifications non enregistrées"
                : enregistre?.modifie_le ? `Note enregistrée à ${heure(enregistre.modifie_le)}` : ""}
            </span>
          </div>
          {conflit && (
            <Encart ton="error" testId="note-visite-conflit">
              La visite a été modifiée depuis son chargement : votre note n'a pas été enregistrée.
              Actualisez avant de réenregistrer.{" "}
              <Button data-testid="note-visite-actualiser" variant="outline" onClick={actualiser} style={SECONDAIRE}>
                Actualiser la note
              </Button>
            </Encart>
          )}
          {texteNonEnregistre && !conflit && texteNonEnregistre !== texte && (
            <div data-testid="note-visite-non-enregistre" className="text-sm"
                 style={{ whiteSpace: "pre-wrap", color: "var(--oh-text-secondary)" }}>
              Votre texte non enregistré (à reprendre si besoin) : {texteNonEnregistre}
            </div>
          )}
        </>
      )}
      {etat && <Encart ton="error" testId="note-visite-erreur">{etat}</Encart>}
    </section>
  );
}

// ── PHOTOS ──────────────────────────────────────────────────────────────────
/** Réduit une photo de téléphone (souvent 3–8 Mo) avant l'envoi ; sans canvas, l'envoie telle quelle. */
async function reduirePhoto(fichier, cote = 1600) {
  try {
    if (typeof createImageBitmap !== "function" || typeof document === "undefined") return { fichier, reduite: false };
    const image = await createImageBitmap(fichier);
    const echelle = Math.min(1, cote / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.width * echelle);
    canvas.height = Math.round(image.height * echelle);
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((ok) => canvas.toBlob(ok, "image/jpeg", 0.82));
    return blob ? { fichier: new File([blob], (fichier.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }), reduite: true } : { fichier, reduite: false };
  } catch { return { fichier, reduite: false }; }
}

/** Nom + type d'un fichier reconstitué depuis une photo RELUE du serveur (extension toujours présente : la galerie en a besoin). */
function fichierDePhoto(photo, blob) {
  const type = blob.type || photo.type || "image/jpeg";
  let nom = photo.nom || `photo-${photo.id}`;
  if (!/\.\w{2,5}$/.test(nom)) nom += "." + ((type.split("/")[1] || "jpg").replace("jpeg", "jpg"));
  return new File([blob], nom, { type });
}

// 🔴 Points 14/15 — la photo reste dans OmniHub (serveur) ET peut être copiée sur le téléphone À TOUT MOMENT, y compris à la réouverture de la visite
//    (les octets sont relus du serveur, ⛔ pas de la file en mémoire qui, elle, est perdue au rechargement) et visite close.
const Vignette = memo(function Vignette({ api, releveId, photo, onSupprimer, lectureSeule, onBlob, compact }) {
  const [charge, setCharge] = useState(null);   // { blob, url } : un seul rendu par photo relue
  const [copie, setCopie] = useState(null);
  const { blob, url } = charge || {};
  useEffect(() => {
    let vivant = true, u = null;
    api.photoBlob(releveId, photo.id).then((b) => {
      if (!vivant) return;
      onBlob?.(photo.id, b);
      if (typeof URL !== "undefined" && URL.createObjectURL) u = URL.createObjectURL(b);
      setCharge({ blob: b, url: u });
    }).catch(() => {});
    return () => { vivant = false; if (u && URL.revokeObjectURL) URL.revokeObjectURL(u); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, releveId, photo.id]);
  const copier = async () => { if (blob) setCopie(await enregistrerSurLeTelephone(fichierDePhoto(photo, blob))); };
  if (compact) {
    return url ? <img data-testid="photo-miniature" src={url} alt={photo.nom || "Photo"} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8 }} />
               : <div data-testid="photo-miniature" style={{ width: 64, height: 64, borderRadius: 8, background: "var(--oh-bg)", border: "0.5px solid var(--oh-border)" }} />;
  }
  return (
    <figure data-testid="photo-releve" style={{ margin: 0, display: "grid", gap: 4 }}>
      {url ? <img src={url} alt={photo.nom || "Photo"} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 8 }} />
           : <div style={{ aspectRatio: "1", borderRadius: 8, background: "var(--oh-bg)", border: "0.5px solid var(--oh-border)" }} />}
      <button type="button" data-testid={`photo-copier-${photo.id}`} disabled={!blob} onClick={copier}
              aria-label="Enregistrer la photo sur le téléphone" className="text-sm"
              style={{ ...DISCRET, border: "0.5px solid var(--oh-border)", borderRadius: 8, minHeight: 32, padding: "4px 8px",
                       cursor: blob ? "pointer" : "default", opacity: blob ? 1 : 0.6 }}>
        Enregistrer sur le téléphone
      </button>
      {copie && <figcaption data-testid="photo-copier-message" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>{copie.message}</figcaption>}
      {!lectureSeule && (
        <Button variant="outline" data-testid={`photo-supprimer-${photo.id}`} onClick={() => onSupprimer(photo.id)}
                aria-label="Supprimer la photo" style={{ ...DISCRET, height: 32 }}>
          Supprimer
        </Button>
      )}
    </figure>
  );
});

const LIMITE_LISTE = 500;   // le serveur ne rend que 500 photos par visite : au-delà, on le DIT au lieu de tronquer en silence

/** L'état de la file d'envoi d'une visite (module `photosEnAttente`, ⛔ pas l'état du composant : survit au menu ☰). */
function useFilePhotos(releveId) {
  return useSyncExternalStore(filePhotos.abonner, () => filePhotos.etat(releveId));
}

/** 🔴 Sur l'écran PRINCIPAL (jamais démontée avec le menu ☰) : « N photo(s) non envoyée(s) » tant qu'il en reste en échec ou en cours. */
export function PhotosNonEnvoyees({ releveId }) {
  const { items: tous } = useFilePhotos(releveId);
  const items = tous.filter((i) => i.etat !== "ok");
  const envoyees = tous.filter((i) => i.etat === "ok");
  return (
    <>
      {items.length > 0 && (() => {
        const echecs = items.filter((i) => i.etat === "echec").length;
        return (
          <Encart ton={echecs ? "error" : "info"} testId="photos-non-envoyees">
            {echecs
              ? `⚠️ ${echecs} photo${echecs > 1 ? "s" : ""} non envoyée${echecs > 1 ? "s" : ""} — elle${echecs > 1 ? "s sont conservées" : " est conservée"} : ouvrez le menu ☰ › Photos pour réessayer ou retirer. Fermer cet onglet les perdrait.`
              : `Envoi de ${items.length} photo${items.length > 1 ? "s" : ""} en cours…`}
          </Encart>
        );
      })()}
      {envoyees.length > 0 && <PhotosEnvoyeesRecentes releveId={releveId} envoyees={envoyees} />}
    </>
  );
}

/** Photos qui viennent d'être envoyées : « Enregistrer sur le téléphone » (copie EXPLICITE, voir copieTelephone.js). */
function PhotosEnvoyeesRecentes({ releveId, envoyees }) {
  const [messages, setMessages] = useState({});
  const copier = async (i) => {
    const r = await enregistrerSurLeTelephone(i.fichier);
    setMessages((m) => ({ ...m, [i.cle]: r }));
  };
  return (
    <div data-testid="photos-envoyees-recentes" style={{ display: "grid", gap: 8 }}>
      {envoyees.map((i) => (
        <div key={i.cle} data-testid="photo-envoyee" className="text-sm"
             style={{ display: "grid", gap: 4, padding: 8, borderRadius: 8, background: "var(--oh-success-bg)", color: "var(--oh-success-text)", border: "0.5px solid var(--oh-success-border)" }}>
          <div>✓ Envoyée dans la visite — {i.nom}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant="outline" data-testid="photo-telephone" style={{ ...DISCRET, height: 32 }} onClick={() => copier(i)}>Enregistrer sur le téléphone</Button>
            <Button variant="outline" data-testid="photo-masquer" style={{ ...DISCRET, height: 32 }} onClick={() => filePhotos.retirer(releveId, i.cle)}>Masquer</Button>
          </div>
          {messages[i.cle] && <div data-testid="photo-telephone-message" style={{ color: "var(--oh-text-secondary)" }}>{messages[i.cle].message}</div>}
        </div>
      ))}
    </div>
  );
}

/** 🔴 Accès PHOTO découvrable sur l'écran principal mobile (pas seulement dans ☰) : ouvre l'appareil photo, la photo est celle de la visite (générale). */
export function BoutonPhotoRapide({ api, releveId, ouvert }) {
  const entree = useRef(null);
  const { items } = useFilePhotos(releveId);
  const envoi = items.some((i) => i.etat === "envoi" || i.etat === "attente");
  if (!ouvert) return null;
  const choisir = (ev) => {
    const fichiers = Array.from(ev.target.files || []);
    ev.target.value = "";                                   // le fichier est déjà dans la file : on peut vider le champ sans risque
    if (fichiers.length) filePhotos.deposer(api, releveId, fichiers, null, reduirePhoto);
  };
  return (
    <div>
      <input ref={entree} data-testid="photo-rapide-fichier" type="file" accept="image/*" capture="environment" multiple
             onChange={choisir} style={{ display: "none" }} aria-label="Prendre une photo" />
      <Button variant="outline" data-testid="photo-rapide" disabled={envoi} onClick={() => entree.current?.click()} style={SECONDAIRE}>
        {envoi ? "Envoi de la photo…" : "📷 Prendre une photo"}
      </Button>
    </div>
  );
}

/** Rappel NON bloquant : aucune photo (serveur ET file d'envoi) sur une visite ouverte. Les photos restent facultatives. */
export function RappelSansPhoto({ api, releveId }) {
  const [nb, setNb] = useState(null);
  const { items, ok } = useFilePhotos(releveId);
  useEffect(() => {
    let vivant = true;
    api.photos(releveId).then((l) => { if (vivant) setNb(l.length); }).catch(() => {});
    return () => { vivant = false; };
  }, [api, releveId, ok]);
  if (nb !== 0 || items.some((i) => i.etat !== "ok")) return null;
  return (
    <Encart ton="info" testId="rappel-sans-photo">
      Aucune photo pour l'instant. Après « Créer le devis », il ne sera plus possible d'en ajouter : prenez-les maintenant si besoin.
    </Encart>
  );
}

/** Photos déjà enregistrées de la visite, en lecture seule compacte (compteur + miniatures) : on voit ce qui est pris AVANT de créer le devis. */
function PhotosEnregistrees({ api, releveId }) {
  const [photos, setPhotos] = useState([]);
  const { ok } = useFilePhotos(releveId);
  useEffect(() => {
    let vivant = true;
    api.photos(releveId).then((l) => { if (vivant) setPhotos(l); }).catch(() => {});
    return () => { vivant = false; };
  }, [api, releveId, ok]);
  if (!photos.length) return null;
  return (
    <div data-testid="photos-enregistrees" style={{ display: "grid", gap: 8 }}>
      <div className="text-sm" data-testid="photos-enregistrees-nombre" style={{ color: "var(--oh-text-secondary)" }}>
        {photos.length} photo{photos.length > 1 ? "s" : ""} enregistrée{photos.length > 1 ? "s" : ""}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {photos.slice(0, 12).map((p) => <Vignette key={p.id} api={api} releveId={releveId} photo={p} lectureSeule compact />)}
      </div>
    </div>
  );
}

/** 🔴 Fin de parcours (dictée → travaux → quantités → PHOTOS → devis) : photos déjà prises, accès 📷, rappel non bloquant — juste avant « Créer le devis ». */
export function ZonePhotosAvantDevis({ api, releveId, ouvert, rappel = true }) {
  return (
    <div data-testid="photos-avant-devis" style={{ display: "grid", gap: 8 }}>
      <PhotosEnregistrees api={api} releveId={releveId} />
      <BoutonPhotoRapide api={api} releveId={releveId} ouvert={ouvert} />
      {rappel && <RappelSansPhoto api={api} releveId={releveId} />}
    </div>
  );
}

export function PhotosReleve({ api, releveId, postes, motPoste = "Poste", lectureSeule }) {
  const [photos, setPhotos] = useState([]);
  const blobs = useRef({});                      // octets relus du serveur, par id de photo (ref : 500 photos ⇒ pas un rendu par photo)
  const [, setNbChargees] = useState(0);
  const flushPrevu = useRef(false);
  const monte = useRef(true);
  useEffect(() => { monte.current = true; return () => { monte.current = false; }; }, []);
  const [copieTout, setCopieTout] = useState(null);
  const [posteId, setPosteId] = useState("");
  const [etat, setEtat] = useState("");
  const entree = useRef(null);
  const { items: tous, ok } = useFilePhotos(releveId);
  const items = tous.filter((i) => i.etat !== "ok");
  const envoi = items.some((i) => i.etat === "envoi" || i.etat === "attente");

  const recharger = () => api.photos(releveId).then(setPhotos).catch((err) => setEtat(err.message));
  // Recharge à l'ouverture ET chaque fois qu'une photo vient d'être envoyée (même si l'envoi a démarré hors de ce composant).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { recharger(); }, [api, releveId, ok]);

  const deposer = (ev) => {
    const fichiers = Array.from(ev.target.files || []);
    ev.target.value = "";                                   // les fichiers sont dans la file (conservés en cas d'échec)
    if (!fichiers.length) return;
    setEtat("");
    filePhotos.deposer(api, releveId, fichiers, posteId || null, reduirePhoto);
  };
  const surBlob = useCallback((pid, b) => {
    blobs.current[pid] = b;
    if (flushPrevu.current) return;
    flushPrevu.current = true;
    queueMicrotask(() => { flushPrevu.current = false; if (monte.current) setNbChargees(Object.keys(blobs.current).length); });
  }, []);
  const chargees = photos.filter((p) => blobs.current[p.id]);
  const toutesChargees = photos.length > 0 && chargees.length === photos.length;
  const toutCopier = async () => setCopieTout(await enregistrerSurLeTelephone(chargees.map((p) => fichierDePhoto(p, blobs.current[p.id]))));
  const supprimer = async (pid) => {
    setEtat("");
    try { await api.supprimerPhoto(releveId, pid); await recharger(); } catch (err) { setEtat(err.message); }
  };
  // Référence stable : les vignettes (mémoïsées) ne se redessinent pas à chaque photo relue (500 photos possibles).
  const supprimerRef = useRef(supprimer);
  supprimerRef.current = supprimer;
  const surSupprimer = useCallback((pid) => supprimerRef.current(pid), []);

  return (
    <section data-testid="photos-releve" style={CARTE}>
      <div style={{ fontWeight: 500 }}>Photos ({photos.length})</div>
      {lectureSeule && (
        <p data-testid="photos-visite-close" className="text-sm" style={{ color: "var(--oh-text-secondary)", margin: 0 }}>
          Visite terminée : aucune photo ne peut plus être ajoutée.
        </p>
      )}
      {!lectureSeule && (
        <div style={{ display: "grid", gap: 8 }}>
          {postes?.length > 0 && (
            <select data-testid="photo-poste" aria-label="Rattacher la photo" value={posteId}
                    onChange={(ev) => setPosteId(ev.target.value)}
                    style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: "0 8px",
                             background: "var(--oh-bg)", color: "var(--oh-text)" }}>
              <option value="">Photo générale de la visite</option>
              {postes.map((p) => <option key={p.id} value={p.id}>{motPoste} : {p.designation}</option>)}
            </select>
          )}
          <input ref={entree} data-testid="photo-fichier" type="file" accept="image/*" capture="environment"
                 multiple onChange={deposer} style={{ display: "none" }} aria-label="Ajouter une photo" />
          <Button variant="outline" data-testid="photo-ajouter" disabled={envoi}
                  onClick={() => entree.current?.click()} style={SECONDAIRE}>
            {envoi ? "Envoi en cours…" : "Prendre ou ajouter une photo"}
          </Button>
        </div>
      )}
      {items.length > 0 && (
        <div data-testid="photos-file" style={{ display: "grid", gap: 8 }}>
          {items.map((i) => (
            <div key={i.cle} data-testid={`photo-file-${i.etat}`} className="text-sm"
                 style={{ display: "grid", gap: 4, padding: 8, borderRadius: 8,
                          background: i.etat === "echec" ? "var(--oh-error-bg)" : "var(--oh-info-bg)",
                          color: i.etat === "echec" ? "var(--oh-error-text)" : "var(--oh-info-text)",
                          border: `0.5px solid var(--oh-${i.etat === "echec" ? "error" : "info"}-border)` }}>
              <div>{i.etat === "echec" ? "Non envoyée" : "Envoi en cours…"} — {i.nom}</div>
              {i.erreur && <div data-testid="photo-file-erreur">{i.erreur}</div>}
              {i.etat === "echec" && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {!i.definitif && <Button variant="outline" data-testid="photo-reessayer" style={{ ...DISCRET, height: 32 }}
                                           onClick={() => filePhotos.reessayer(api, releveId, i.cle, reduirePhoto)}>Réessayer</Button>}
                  <Button variant="outline" data-testid="photo-retirer" style={{ ...DISCRET, height: 32 }}
                          onClick={() => filePhotos.retirer(releveId, i.cle)}>Retirer</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {photos.length > 1 && (
        <div style={{ display: "grid", gap: 4 }}>
          <Button variant="outline" data-testid="photos-tout-copier" disabled={!toutesChargees} onClick={toutCopier} style={SECONDAIRE}>
            Tout enregistrer sur le téléphone ({photos.length})
          </Button>
          {copieTout && <div data-testid="photos-tout-copier-message" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>{copieTout.message}</div>}
        </div>
      )}
      {photos.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
          {photos.map((p) => <Vignette key={p.id} api={api} releveId={releveId} photo={p}
                                        onSupprimer={surSupprimer} lectureSeule={lectureSeule} onBlob={surBlob} />)}
        </div>
      ) : <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Aucune photo.</p>}
      {photos.length >= LIMITE_LISTE && (
        <Encart ton="info" testId="photos-limite">Seules les {LIMITE_LISTE} premières photos de la visite sont affichées ; les suivantes sont bien enregistrées.</Encart>
      )}
      {etat && <Encart ton="error" testId="photos-erreur">{etat}</Encart>}
    </section>
  );
}

// ── CLÔTURE : récapitulatif PUIS confirmation ───────────────────────────────
export function TerminerVisite({ api, releve, motPoste = "Poste", onClos }) {
  const [recap, setRecap] = useState(null);
  const [etat, setEtat] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const preparer = async () => {
    setEtat("");
    try {
      const [photos, note] = await Promise.all([api.photos(releve.id), api.lireNote(releve.id)]);
      const postes = releve.postes || [];
      setRecap({ postes: postes.length, sansQuantite: postes.filter((p) => p.quantite === null || p.quantite === undefined).map((p) => p.designation),
                 photos: photos.length, note: Boolean((note.texte || "").trim()) });
    } catch (err) { setEtat(err.message); }
  };
  const confirmer = async () => {
    setEtat(""); setEnvoi(true);
    try { onClos(await api.terminer(releve.id)); } catch (err) { setEtat(err.message); } finally { setEnvoi(false); }
  };

  if (!recap) {
    return (
      <section style={{ display: "grid", gap: 8 }}>
        <Button data-testid="terminer-visite" variant="outline" onClick={preparer} style={SECONDAIRE}>
          Terminer la visite
        </Button>
        {etat && <Encart ton="error" testId="terminer-erreur">{etat}</Encart>}
      </section>
    );
  }
  return (
    <section data-testid="recapitulatif-cloture" style={{ ...CARTE, border: "0.5px solid var(--oh-accent)" }}>
      <div style={{ fontWeight: 500 }}>Récapitulatif avant clôture</div>
      <ul className="text-sm" style={{ lineHeight: 1.6 }}>
        <li data-testid="recap-postes">{recap.postes} {motPoste.toLowerCase()}(s) enregistré(s)</li>
        {recap.sansQuantite.length > 0 && (
          <li data-testid="recap-sans-quantite">Sans quantité : {recap.sansQuantite.join(", ")}</li>
        )}
        <li data-testid="recap-photos">{recap.photos} photo(s)</li>
        <li data-testid="recap-note">{recap.note ? "Note de visite renseignée" : "Aucune note de visite"}</li>
      </ul>
      <Encart ton="info">Une fois close, la visite ne pourra plus être modifiée ; elle restera consultable.</Encart>
      {recap.photos === 0 && (
        <Encart ton="info" testId="recap-sans-photo">
          Aucune photo n'a été prise. Après la clôture, il ne sera plus possible d'en ajouter : annulez pour
          en prendre, ou confirmez la clôture sans photo.
        </Encart>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button data-testid="confirmer-cloture" onClick={confirmer} disabled={envoi} style={PRIMAIRE}>
          Confirmer la clôture
        </Button>
        <Button data-testid="annuler-cloture" variant="outline" onClick={() => setRecap(null)} disabled={envoi} style={DISCRET}>
          Annuler
        </Button>
      </div>
      {etat && <Encart ton="error" testId="terminer-erreur">{etat}</Encart>}
    </section>
  );
}

// ── RELEVÉS DU DOSSIER (en cours / clos) ────────────────────────────────────
export function RelevesDuDossier({ releves, actif, onConsulter }) {
  if (!releves?.length) return null;
  return (
    <section data-testid="releves-dossier" style={{ display: "grid", gap: 4 }}>
      <div className="text-sm" style={{ color: "var(--oh-text-secondary)", fontWeight: 500 }}>Relevés du dossier</div>
      {releves.map((r) => (
        <button key={r.id} type="button" data-testid={`releve-${r.etat}`} onClick={() => onConsulter(r.id)}
                style={{ textAlign: "left", borderRadius: 8, padding: 8, cursor: "pointer",
                         border: `0.5px solid ${r.id === actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                         background: r.id === actif ? "var(--oh-accent-bg)" : "transparent",
                         color: r.id === actif ? "var(--oh-accent-text)" : "var(--oh-text)" }}>
          {r.etat === "clos" ? `Relevé clos le ${date(r.clos_le)}` : "Relevé en cours"} · {r.nombre_postes} poste(s)
        </button>
      ))}
    </section>
  );
}
